// One way to print a metric, shared by every page, so the same number never
// reads "Rp 1,2 M" on one screen and "1.200.000.000" on another.
import type { MetricFormat, Verdict } from "@dashboard-intelligent/api/src/modules/industry/types";

const id = (options: Intl.NumberFormatOptions) => new Intl.NumberFormat("id-ID", options);

const plain = id({ maximumFractionDigits: 0 });
const oneDecimal = id({ maximumFractionDigits: 1 });
const twoDecimals = id({ maximumFractionDigits: 2 });

/** Rupiah, shortened from a million up: Rp 950.000, Rp 12,5 jt, Rp 1,2 M, Rp 3,4 T. */
export function formatIdr(value: number, short = true): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (!short || abs < 1e6) return `${sign}Rp ${plain.format(abs)}`;
  if (abs < 1e9) return `${sign}Rp ${oneDecimal.format(abs / 1e6)} jt`;
  if (abs < 1e12) return `${sign}Rp ${oneDecimal.format(abs / 1e9)} M`;
  return `${sign}Rp ${oneDecimal.format(abs / 1e12)} T`;
}

/**
 * A metric value as text. "percent" is 0 to 100 on the wire (the contract's
 * scale), so 49.9 prints as "49,9%". Null prints as "Tidak tersedia", never
 * as 0, and the page pairs it with the metric's own note.
 */
export function formatMetric(value: number | null, format: MetricFormat, options: { short?: boolean } = {}): string {
  if (value === null || !Number.isFinite(value)) return "Tidak tersedia";
  switch (format) {
    case "idr":
      return formatIdr(value, options.short ?? true);
    case "percent":
      return `${oneDecimal.format(value)}%`;
    case "count":
      return plain.format(value);
    case "decimal":
      return twoDecimals.format(value);
    case "minutes":
      return `${oneDecimal.format(value)} menit`;
    case "hours":
      return `${oneDecimal.format(value)} jam`;
    case "days":
      return `${oneDecimal.format(value)} hari`;
    case "score":
      return oneDecimal.format(value);
  }
}

/** A formatter for chart axes and data tables, which take (n: number) => string. */
export function metricFormatter(format: MetricFormat): (n: number) => string {
  return (n: number) => formatMetric(n, format);
}

/** The kit's VerdictBadge reads these; exported so pages can say them in sentences too. */
export const VERDICT_TEXT: Record<Verdict, string> = {
  good: "Berjalan baik",
  watch: "Perlu dipantau",
  attention: "Perlu tindakan",
};
